import { get, intersection, negate } from 'lodash-es';
import { getIsNonTMVDealer } from 'site-modules/shared/utils/inventory/vdp-utils';

const VALID_LEASE_INCENTIVE_TYPES = ['CUSTOMER_CASH', 'CUSTOMER_CASH_OR_APR', 'CUSTOMER_BONUS_CASH'];
const LEASE_INCENTIVES_TRANSACTION_TYPES = ['LEASE'];
const PRIMARY_LEASE_SUBTYPE_IDS = [605, 655, 606, 625, 624, 626, 664, 19, 603];

export const getSelectedIncentives = ({ vinData, ids = [] }) =>
  get(vinData, 'availableIncentives', []).filter(({ id }) => ids?.includes(id));

function getLoanIncentives(vinData) {
  const LOAN_INCENTIVES_TRANSACTION_TYPES = ['FINANCE', 'CASH'];
  const allIncentives = get(vinData, 'availableIncentives', []);
  return allIncentives.filter(
    ({ transactionTypes }) => !!intersection(transactionTypes, LOAN_INCENTIVES_TRANSACTION_TYPES).length
  );
}

function getLeaseIncentives(vinData) {
  const allIncentives = get(vinData, 'availableIncentives', []);
  return allIncentives.filter(
    ({ transactionTypes, type, inventoryCode }) =>
      !!intersection(transactionTypes, LEASE_INCENTIVES_TRANSACTION_TYPES).length &&
      VALID_LEASE_INCENTIVE_TYPES.includes(type) &&
      inventoryCode?.toLowerCase() === 'new'
  );
}

export const isIncentivePrimary = incentive => incentive.primary;

const isLegacyCarBuyingLeaseIncentive = incentive =>
  'incentiveId' in incentive || 'programId' in incentive || 'cash' in incentive;

export const isLeaseIncentivePrimary = incentive => {
  if (isLegacyCarBuyingLeaseIncentive(incentive)) {
    return !incentive.conditionCategory;
  }

  return PRIMARY_LEASE_SUBTYPE_IDS.includes(Number(incentive.subtypeId)) && !incentive.optionsBased;
};

export const filterInPrimaryIncentives = incentives => incentives.filter(isIncentivePrimary);

export const filterOutPrimaryIncentives = incentives => incentives.filter(negate(isIncentivePrimary));

export const filterInPrimaryLeaseIncentives = incentives => incentives.filter(isLeaseIncentivePrimary);

export const filterOutPrimaryLeaseIncentives = incentives => incentives.filter(negate(isLeaseIncentivePrimary));

export function getLoanPrimaryIncentives({ vinData }) {
  const franchiseId = get(vinData, 'dealerInfo.franchiseId', '');
  const isNonTMVDealer = getIsNonTMVDealer(franchiseId);
  const incentives = getLoanIncentives(vinData);
  return isNonTMVDealer ? [] : filterInPrimaryIncentives(incentives);
}

export function getLoanConditionalIncentives({ vinData }) {
  const franchiseId = get(vinData, 'dealerInfo.franchiseId', '');
  const isNonTMVDealer = getIsNonTMVDealer(franchiseId);
  const allIncentives = getLoanIncentives(vinData);
  return isNonTMVDealer ? allIncentives : filterOutPrimaryIncentives(allIncentives);
}

export function getLeasePrimaryIncentives({ vinData }) {
  const franchiseId = get(vinData, 'dealerInfo.franchiseId', '');
  const isNonTMVDealer = getIsNonTMVDealer(franchiseId);
  const incentives = getLeaseIncentives(vinData);
  return isNonTMVDealer ? [] : filterInPrimaryLeaseIncentives(incentives);
}

export function getLeaseConditionalIncentives({ vinData }) {
  const franchiseId = get(vinData, 'dealerInfo.franchiseId', '');
  const isNonTMVDealer = getIsNonTMVDealer(franchiseId);
  const allIncentives = getLeaseIncentives(vinData);
  return isNonTMVDealer ? allIncentives : filterOutPrimaryLeaseIncentives(allIncentives);
}

function getIncompatibleIncentives({ selectedIncentives, allIncentives }) {
  const selectedIncentivesIds = selectedIncentives.map(({ id }) => id);

  return allIncentives
    .filter(({ id }) => !selectedIncentivesIds.includes(id))
    .filter(({ id }) => !selectedIncentives.every(({ compatibleIncentiveIds }) => compatibleIncentiveIds.includes(id)));
}

export function getIncompatibleIncentivesIds({ selectedIncentives, allIncentives }) {
  const incompatibleIncentivesIds = {};
  getIncompatibleIncentives({ selectedIncentives, allIncentives }).forEach(({ id: incentiveId }) => {
    incompatibleIncentivesIds[incentiveId] = true;
  });
  return incompatibleIncentivesIds;
}

export function isOtherRebate({ type }) {
  return type === 'OTHER_OFFERS';
}

export function filterLeaseIncentivesByTerm(incentives, selectedLeaseTerm) {
  return incentives.filter(({ termMonths }) => !termMonths || termMonths === selectedLeaseTerm);
}
